import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ onContactOpen }) {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Top meta row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] text-[#475569] mb-10 md:mb-16"
        >
          <span data-testid="hero-eyebrow">Issue 01 — Storytelling for Growth</span>
          <span className="hidden md:inline">Est. by Rachel · 20+ Years</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Headline column */}
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE }}
              data-testid="hero-headline"
              className="font-display font-light tracking-tighter leading-[0.92] text-[#0F172A] text-[clamp(2.75rem,8vw,7rem)]"
            >
              Your business <br />
              has <span className="italic text-[#C86A53]">stories</span> <br />
              worth telling.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
              data-testid="hero-subheading"
              className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#475569] font-light"
            >
              The most successful organisations are the ones people understand.
              We help growing businesses turn expertise, experiences and moments into
              stories that build trust, strengthen relationships and support growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-6"
            >
              <button
                data-testid="hero-cta"
                onClick={onContactOpen}
                className="group inline-flex items-center gap-3 bg-[#C86A53] hover:bg-[#A65541] text-[#F9F6F0] px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
              >
                Let's Talk About Growth
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                data-testid="hero-secondary"
                href="#problem"
                className="awd-link text-xs uppercase tracking-[0.22em] font-semibold text-[#0F172A]"
              >
                See How It Works
              </a>
            </motion.div>
          </div>

          {/* Image column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden aspect-[4/5] border border-[#E2DCD0]">
              <img
                src="https://customer-assets.emergentagent.com/job_brief-storytelling/artifacts/porvctcc_storytellingsmall.jpeg"
                alt="A woman in a red sweater smiling warmly with a drink, surrounded by plants — storytelling that connects"
                className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
                loading="eager"
              />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-[#0F172A]/70 to-transparent">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#F9F6F0]/90">
                  Storytelling — In Practice
                </p>
              </div>
            </div>
            {/* Caption */}
            <p className="mt-4 text-xs italic text-[#475569]">
              "When people understand your story, they're more likely to engage with it."
            </p>
          </motion.div>
        </div>

        {/* Bottom marquee strip */}
        <div className="mt-20 md:mt-28 border-y border-[#E2DCD0] py-5 overflow-hidden">
          <div className="awd-marquee whitespace-nowrap font-display text-2xl md:text-3xl text-[#0F172A]/80">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-8 pr-8">
                <span>Trust.</span>
                <span className="text-[#C86A53]">·</span>
                <span className="italic">Clarity.</span>
                <span className="text-[#C86A53]">·</span>
                <span>Relationships.</span>
                <span className="text-[#C86A53]">·</span>
                <span className="italic">Reputation.</span>
                <span className="text-[#C86A53]">·</span>
                <span>Engagement.</span>
                <span className="text-[#C86A53]">·</span>
                <span className="italic">Growth.</span>
                <span className="text-[#C86A53]">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
